var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useCallback } from 'preact/hooks';
import { getSearchProvider } from './providers';
/**
 * Query keys that must never be treated as facet filters (marketing / attribution).
 * Filter params are namespaced with `URL_FILTER_PARAM_PREFIX` (`mrf_`) on write
 * via `updateURLWithFilters`, so this denylist is only here as a safety net for
 * the read path — any non-prefixed key landing in the URL falls through to here.
 */
const TRACKING_URL_PARAM_KEYS = new Set([
    'utm_source',
    'utm_medium',
    'utm_campaign',
    '_kx',
]);
function isTrackingUrlParamKey(key) {
    return TRACKING_URL_PARAM_KEYS.has(key.toLowerCase());
}
export function useSearchProvider(providerType, mockPlatform, initialSearchConfig) {
    // Core search state
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [filters, setFilters] = useState([]);
    const [meta, setMeta] = useState(null);
    const [sortOptions, setSortOptions] = useState([]);
    const [provider] = useState(() => getSearchProvider(providerType, mockPlatform));
    // Search parameters state
    const [searchState, setSearchState] = useState({
        query: '',
        currentSort: 'relevance',
        appliedFilters: {},
        collectionId: ''
    });
    // URL parameter management
    const updateUrlParams = useCallback(({ query, currentSort, appliedFilters }) => {
        const urlParams = new URLSearchParams();
        if (currentSort) {
            urlParams.set('sort', currentSort);
        }
        if (query) {
            urlParams.set('q', query);
        }
        Object.entries(appliedFilters).forEach(([key, values]) => {
            values.forEach(value => {
                urlParams.append(key, value);
            });
        });
        window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
    }, []);
    // Initialize from URL parameters
    useEffect(() => {
        var _a, _b, _c;
        const urlParams = new URLSearchParams(window.location.search);
        const queryParam = urlParams.get('q');
        const sortParam = urlParams.get('sort');
        const initialFilters = {};
        urlParams.forEach((value, key) => {
            if (key === 'q' || key === 'sort')
                return;
            if (isTrackingUrlParamKey(key))
                return;
            if (!initialFilters[key]) {
                initialFilters[key] = [];
            }
            initialFilters[key].push(value);
        });
        // Get collectionId from URL params, initialSearchConfig, or dataset
        let collectionId = initialSearchConfig === null || initialSearchConfig === void 0 ? void 0 : initialSearchConfig.collectionId;
        if (!collectionId) {
            const searchApp = document.querySelector('#search-app');
            collectionId = (_a = searchApp === null || searchApp === void 0 ? void 0 : searchApp.dataset) === null || _a === void 0 ? void 0 : _a.collectionId;
        }
        // If collectionId is found, leave query empty; otherwise use queryParam or initialSearchConfig
        const query = collectionId ? '' : ((_b = queryParam !== null && queryParam !== void 0 ? queryParam : initialSearchConfig === null || initialSearchConfig === void 0 ? void 0 : initialSearchConfig.query) !== null && _b !== void 0 ? _b : '');
        // Use initialSearchConfig values if provided, otherwise fall back to URL params or defaults
        const newSearchState = {
            query: query,
            currentSort: (_c = sortParam !== null && sortParam !== void 0 ? sortParam : initialSearchConfig === null || initialSearchConfig === void 0 ? void 0 : initialSearchConfig.currentSort) !== null && _c !== void 0 ? _c : '',
            appliedFilters: initialFilters !== null && initialFilters !== void 0 ? initialFilters : initialSearchConfig === null || initialSearchConfig === void 0 ? void 0 : initialSearchConfig.appliedFilters,
            collectionId: collectionId
        };
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(newSearchState.collectionId ? '' : newSearchState.query, {
            sort: newSearchState.currentSort || undefined,
            filters: newSearchState.appliedFilters,
            pageSize: initialSearchConfig === null || initialSearchConfig === void 0 ? void 0 : initialSearchConfig.pageSize,
            collectionId: newSearchState.collectionId
        });
    }, []);
    // Core search function
    const runSearch = (query_1, ...args_1) => __awaiter(this, [query_1, ...args_1], void 0, function* (query, options = {}) {
        setLoading(true);
        try {
            yield provider.search(query, options);
            setProducts(provider.getProducts());
            setFilters(provider.getFilters());
            setMeta(provider.getMeta());
            setSortOptions(provider.getSortOptions());
        }
        catch (error) {
            console.error('Search failed:', error);
        }
        finally {
            setLoading(false);
        }
    });
    // Event handlers
    const handleQuerySubmit = (newQuery) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { query: newQuery });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        // Don't pass query if collectionId is present. PLP does not have a search query input.
        runSearch(searchState.collectionId ? '' : newQuery, {
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters,
            collectionId: searchState.collectionId
        });
    };
    const handleFilterChange = (filterId, value) => {
        setSearchState(prev => {
            var _a;
            const newFilters = Object.assign({}, prev.appliedFilters);
            if (filterId.includes('price')) {
                newFilters[filterId] = [value];
            }
            else {
                if ((_a = newFilters[filterId]) === null || _a === void 0 ? void 0 : _a.includes(value)) {
                    newFilters[filterId] = newFilters[filterId].filter(v => v !== value);
                    if (newFilters[filterId].length === 0) {
                        delete newFilters[filterId];
                    }
                }
                else {
                    newFilters[filterId] = [...(newFilters[filterId] || []), value];
                }
            }
            const newSearchState = Object.assign(Object.assign({}, prev), { appliedFilters: newFilters });
            updateUrlParams(newSearchState);
            runSearch(newSearchState.collectionId ? '' : prev.query, {
                sort: prev.currentSort || undefined,
                filters: newFilters,
                collectionId: newSearchState.collectionId
            });
            return newSearchState;
        });
    };
    const handlePageChange = (page) => {
        runSearch(searchState.collectionId ? '' : searchState.query, {
            page,
            sort: searchState.currentSort || undefined,
            filters: searchState.appliedFilters,
            collectionId: searchState.collectionId
        });
    };
    const handleSortChange = (sort) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { currentSort: sort });
        setSearchState(newSearchState);
        updateUrlParams(newSearchState);
        runSearch(searchState.collectionId ? '' : searchState.query, {
            sort: sort || undefined,
            filters: searchState.appliedFilters,
            collectionId: searchState.collectionId
        });
    };
    return {
        loading,
        products,
        filters,
        meta,
        sortOptions,
        query: searchState.query,
        currentSort: searchState.currentSort,
        appliedFilters: searchState.appliedFilters,
        handleQuerySubmit,
        handleFilterChange,
        handlePageChange,
        handleSortChange,
        runSearch
    };
}
export function useSuggestionsProvider(providerType, mockPlatform, initialSearchConfig) {
    // Core search state
    // Suggestions should not have sorts, filters, page sizes or parameters
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [suggestedTerms, setSuggestedTerms] = useState([]);
    const [suggestedExtras, setSuggestedExtras] = useState([]);
    const [provider] = useState(() => getSearchProvider(providerType, mockPlatform));
    // Suggestions parameters state
    const [searchState, setSearchState] = useState({
        query: '',
        currentSort: '',
        appliedFilters: {},
    });
    useEffect(() => {
        var _a;
        const initialFilters = {};
        const query = (_a = initialSearchConfig === null || initialSearchConfig === void 0 ? void 0 : initialSearchConfig.query) !== null && _a !== void 0 ? _a : '';
        // Use initialSearchConfig values if provided, otherwise fall back to defaults
        const newSearchState = {
            query: query,
            currentSort: '',
            appliedFilters: initialFilters !== null && initialFilters !== void 0 ? initialFilters : initialSearchConfig === null || initialSearchConfig === void 0 ? void 0 : initialSearchConfig.appliedFilters,
        };
        if (query) {
            setSearchState(newSearchState);
            getSuggestions(query);
        }
    }, []);
    // Core search function
    const getSuggestions = (query_1, ...args_1) => __awaiter(this, [query_1, ...args_1], void 0, function* (query, options = {}) {
        setLoading(true);
        try {
            yield provider.fetchSuggestions(query, options);
            setProducts(provider.getProducts());
            setSuggestedTerms(provider.getSuggestedTerms());
            setSuggestedExtras(provider.getSuggestedExtras());
        }
        catch (error) {
            console.error('Suggestions failed:', error);
        }
        finally {
            setLoading(false);
        }
    });
    // Event handlers
    const handleSuggestionQuerySubmit = (newQuery) => {
        const newSearchState = Object.assign(Object.assign({}, searchState), { query: newQuery });
        setSearchState(newSearchState);
        getSuggestions(newQuery);
    };
    return {
        loading,
        suggestedProducts: products,
        suggestedTerms,
        suggestedExtras,
        suggestedQuery: searchState.query,
        handleSuggestionQuerySubmit
    };
}
export function useRecsProvider(providerType, mockPlatform, recsConfig) {
    // Core state
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [meta, setMeta] = useState(null);
    const [provider] = useState(() => getSearchProvider(providerType, mockPlatform));
    // Load recommendations
    const loadRecs = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        setLoading(true);
        try {
            const recsApp = document.querySelector('#recommendation-app');
            // Get seedProductId from recsConfig or dataset (data-seed-product-id)
            const seedProductId = (recsConfig === null || recsConfig === void 0 ? void 0 : recsConfig.seedProductId) || ((_a = recsApp === null || recsApp === void 0 ? void 0 : recsApp.dataset) === null || _a === void 0 ? void 0 : _a.seedProductId);
            // Get recsType from recsConfig or dataset (data-recs-type)
            const recsType = (recsConfig === null || recsConfig === void 0 ? void 0 : recsConfig.recsType) || ((_b = recsApp === null || recsApp === void 0 ? void 0 : recsApp.dataset) === null || _b === void 0 ? void 0 : _b.recommendationType);
            if (seedProductId) {
                yield provider.fetchRecs(seedProductId, recsType);
                setProducts(provider.getRecs());
                setMeta(provider.getMeta());
            }
        }
        catch (error) {
            console.error('Failed to load recommendations:', error);
        }
        finally {
            setLoading(false);
        }
    }), [provider, recsConfig === null || recsConfig === void 0 ? void 0 : recsConfig.seedProductId, recsConfig === null || recsConfig === void 0 ? void 0 : recsConfig.recsType]);
    // Helper function to refresh recs from outside the Preact component
    const refresh = useCallback(() => {
        const recsApp = document.querySelector('#recommendation-app');
        if (!recsApp) {
            console.warn('[refresh] #recommendation-app element not found');
            return;
        }
        const refreshFn = recsApp === null || recsApp === void 0 ? void 0 : recsApp.__refreshRecs;
        if (refreshFn && typeof refreshFn === 'function') {
            refreshFn();
        }
        else {
            console.warn('[refresh] Refresh function not available. Make sure the recs component is mounted.');
        }
    }, []);
    // Initialize recs on mount or when config changes, attach refresh function, and make it globally available
    useEffect(() => {
        const recsApp = document.querySelector('#recommendation-app');
        // Only load recs if await-refresh attribute is not present
        if (!(recsApp === null || recsApp === void 0 ? void 0 : recsApp.hasAttribute('await-refresh'))) {
            loadRecs();
        }
        if (recsApp) {
            recsApp.__refreshRecs = loadRecs;
        }
        // Make refresh globally available via window.mrRecommendations
        if (typeof window !== 'undefined') {
            if (!window.mrRecommendations) {
                window.mrRecommendations = {};
            }
            window.mrRecommendations.refresh = refresh;
        }
        return () => {
            if (recsApp) {
                delete recsApp.__refreshRecs;
            }
        };
    }, [loadRecs, refresh]);
    return {
        loading,
        products,
        meta,
        loadRecs
    };
}
//
